/**
 * WordPress dependencies
 */
/**
 * External dependencies
 */
import mapboxgl from 'mapbox-gl';
import { store, getContext, getElement } from '@wordpress/interactivity';

const STATE_BOUNDS = {
	'new-york': {
		center: [ -73.9442, 40.7282 ],
		zoom: 8.5,
	},
	florida: {
		center: [ -80.2370, 26.1276 ],
		zoom: 8.5,
	},
};

const { state, actions, callbacks } = store( 'photobooths/service-areas', {
	state: {
		selectedCity: null,
		selectedCityTitle: '',
		selectedCityDescription: '',
		selectedCityLink: '',
		selectedCityImage: '',
		markers: [],
		get isActiveState() {
			const context = getContext();
			const { ref } = getElement();

			if ( ! ref || ! ref.dataset || ! ref.dataset.state ) {
				return false;
			}

			return context.activeState === ref.dataset.state;
		},
		get activeCities() {
			const context = getContext();
			const cities = context.cities[ context.activeState ] || [];
			
			// Map cities to include link and hasLink properties
			return cities.map( city => {
				const link = context.cityLinks[ city.name ];
				const hasLink = !! link && link !== '#';
				return {
					...city,
					link: link || '#',
					hasLink,
				};
			});
		},
	},
	actions: {
		switchState: ( event ) => {
			const context = getContext();
			const button = event.target.closest( 'button' );
			const newState = button.dataset.state;

			context.activeState = newState;

			// Close info card when switching states
			state.selectedCity = null;

			// Fly to the new state's location
			if ( context.map && STATE_BOUNDS[ newState ] ) {
				context.map.flyTo( {
					center: STATE_BOUNDS[ newState ].center,
					zoom: STATE_BOUNDS[ newState ].zoom,
					duration: 1500,
				} );
			}

			// Update markers visibility
			actions.updateMarkers( context );
		},
		
		closeInfoCard: () => {
			state.selectedCity = null;
			
			// Return to state view
			const context = getContext();
			if ( context.map && STATE_BOUNDS[ context.activeState ] ) {
				context.map.flyTo( {
					center: STATE_BOUNDS[ context.activeState ].center,
					zoom: STATE_BOUNDS[ context.activeState ].zoom,
					duration: 1000,
				} );
			}
		},

		updateMarkers: ( ctx = null ) => {
			const context = ctx || getContext();
			const activeCities = context.cities[ context.activeState ];

			// Remove all existing markers
			if ( state.markers.length > 0 ) {
				state.markers.forEach( ( marker ) => marker.remove() );
				state.markers = [];
			}

			// Add markers for the active state
			if ( activeCities && context.map ) {
				activeCities.forEach( ( city ) => {
					const el = document.createElement( 'div' );
					el.className = 'service-areas-marker';

					const marker = new mapboxgl.Marker( el )
						.setLngLat( [ city.lng, city.lat ] )
						.addTo( context.map );

					// Add click handler for marker
					el.addEventListener( 'click', () => {
						state.selectedCity = city.name;
						state.selectedCityTitle = `${ city.name } Photo Booth Rental`;
						state.selectedCityDescription = `Rent photo booths for any event in ${ city.name }. Create unforgettable memories with PhotoBoothRentals.com!`;

						const cityLink = context.cityLinks[ city.name ];
						state.selectedCityLink = cityLink || '#';
						
						// Get the featured image from the page, or set to empty string
						state.selectedCityImage = context.cityImages[ city.name ] || '';

						if ( context.map ) {
							context.map.flyTo( {
								center: [ city.lng, city.lat ],
								zoom: 11,
								duration: 1000,
							} );
						}
					} );

					state.markers.push( marker );
				} );
			}
		},

		selectCity: ( event ) => {
			event.preventDefault();
			const context = getContext();
			const city = context.city;

			// Mark this city as selected
			state.selectedCity = city.name;
			state.selectedCityTitle = `${ city.name } Photo Booth Rental`;
			state.selectedCityDescription = `Rent photo booths for any event in ${ city.name }. Create unforgettable memories with PhotoBoothRentals.com!`;

			state.selectedCityLink = city.link;
			
			// Get the featured image from the page, or set to empty string
			state.selectedCityImage = context.cityImages[ city.name ] || '';

			// Fly to the city on the map
			if ( context.map ) {
				context.map.flyTo( {
					center: [ city.lng, city.lat ],
					zoom: 11,
					duration: 1000,
				} );
			}
		},

		highlightCity: ( event ) => {
			const context = getContext();
			const city = context.city;

			// Highlight the marker on the map
			if ( context.map ) {
				context.map.flyTo( {
					center: [ city.lng, city.lat ],
					zoom: 10,
					duration: 800,
				} );
			}
		},

		unhighlightCity: () => {
			const context = getContext();

			// Return to state view
			if ( context.map && STATE_BOUNDS[ context.activeState ] ) {
				context.map.flyTo( {
					center: STATE_BOUNDS[ context.activeState ].center,
					zoom: STATE_BOUNDS[ context.activeState ].zoom,
					duration: 800,
				} );
			}
		},
	},
	callbacks: {
		async init() {
			const context = getContext();

			// Set Mapbox access token from context
			mapboxgl.accessToken = context.mapboxToken;

			// Initialize map
			const mapContainer = document.getElementById( 'service-areas-map' );

			if ( ! mapContainer ) {
				return;
			}

			const initialState = context.activeState || 'new-york';
			const bounds = STATE_BOUNDS[ initialState ];

			context.map = new mapboxgl.Map( {
				container: 'service-areas-map',
				style: 'mapbox://styles/mapbox/light-v11',
				center: bounds.center,
				zoom: bounds.zoom,
				attributionControl: false,
			} );

			// Add navigation controls
			context.map.addControl( new mapboxgl.NavigationControl(), 'top-right' );

			// Wait for map to load before adding markers
			// Capture context before async operation
			context.map.on( 'load', () => {
				actions.updateMarkers( context );
			} );
		},
	},
}, { lock: true } );
